import { getPriceItems } from 'api/v2/revenues';
import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import { components, Select } from 'components/atoms/Form/Select';
import { Loader } from 'components/atoms/Loader';
import SimpleError from 'components/molecules/SimpleError.component';
import { useCallback, useRef, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { RevenuesCacheKeys } from 'types/cacheKeys.types';
import { Currency } from 'types/global.types';
import type { InvoiceTypeEnum, PriceItem } from 'types/invoice.types';
import { TracingConfig } from 'utils/amplitude/amplitude.types';
import formatMoney from 'utils/formatMoney';
import { EditOutlineIcon, EmailInboxOutline } from 'utils/icons';
import { divideBy1000 } from 'utils/math';
import { FIVE_MINUTES } from 'utils/time';

type Props = {
  onItemSelect: (item: PriceItem) => void;
  tracing?: TracingConfig<'onChange' | 'onFocus' | 'onEdit'>;
  currency?: Currency;
  autoFocus?: boolean;
  onEdit?: (_id: string) => void;
  categoryRevenueTypes?: InvoiceTypeEnum | InvoiceTypeEnum[];
};
type OptionType = {
  label: string;
  value: string;
  item: PriceItem;
};

export const ItemSearchField: FC<Props> = ({
  onItemSelect,
  tracing,
  currency,
  autoFocus,
  onEdit,
  categoryRevenueTypes,
}) => {
  const { t } = useTranslation();

  const [search, setSearch] = useState('');

  const hasSavedItemsQuery = useQuery(
    [RevenuesCacheKeys.HasSavedPriceItems, categoryRevenueTypes],
    () => {
      return getPriceItems({ page: 1, perPage: 1, categoryRevenueTypes }).then(
        ({ paging }) => !!paging.totalCount,
      );
    },
    {
      cacheTime: FIVE_MINUTES,
    },
  );

  const onEditRef = useRef(onEdit);
  onEditRef.current = onEdit;

  const dropdownQueryRef = useRef();

  const Option: typeof components.Option = useCallback(
    (props) => {
      const option = props.data as unknown as OptionType;
      const item = option.item;
      return (
        <components.Option {...props}>
          <div
            title={option.label}
            className="flex justify-between items-center"
            tabIndex={props.isFocused ? 0 : -1}
          >
            <div className="flex flex-col leading-tight text-sm">
              <span className="whitespace-break-spaces break-word font-semibold line-clamp-3">
                {option.label}
              </span>
              <span className="text-primary-500">
                {formatMoney(divideBy1000(item.unitAmountExclVAT), currency)}
              </span>
            </div>
            <Button
              tabIndex={props.isFocused ? 0 : -1}
              asDefault
              tracingEvent={tracing?.onEdit}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (!option?.item?._id) return;
                onEditRef.current?.(option.item._id);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  e.preventDefault();
                  if (!option?.item?._id) return;
                  onEditRef.current?.(option.item._id);
                }
              }}
              className={classNames(
                'bg-taxes-200 p-1 rounded-md hover:bg-taxes-300 focus:bg-taxes-300',
              )}
            >
              <EditOutlineIcon className="w-5 h-5 text-taxes" />
            </Button>
          </div>
        </components.Option>
      );
    },
    [currency],
  );

  if (hasSavedItemsQuery.isLoading) {
    return (
      <div className="flex-1 flex justify-center items-center">
        <Loader size="3xl" />
      </div>
    );
  }

  if (hasSavedItemsQuery.isError) {
    return (
      <div className="flex-1 flex justify-center items-center">
        <SimpleError
          messageClassName="!text-xl"
          onRetry={() => hasSavedItemsQuery.refetch()}
        />
      </div>
    );
  }

  if (!hasSavedItemsQuery.data) {
    return (
      <div className="flex-1 flex flex-col text-center justify-center items-center text-main font-avenir">
        <div className="w-12 h-12 bg-taxes-100 relative rounded-full mb-4">
          <EmailInboxOutline className="text-taxes abs-center w-8 h-8" />
        </div>
        <p className="font-semibold leading-tight mb-2">
          {t('invoice.form.add_item_from_catalog.empty.title')}
        </p>
        <p className="text-sm leading-tight">
          {t('invoice.form.add_item_from_catalog.empty.desc')}
        </p>
      </div>
    );
  }

  return (
    <Select
      name="ItemSearchField"
      mode="asyncOptionsPaginated"
      queryRef={dropdownQueryRef}
      tracing={tracing}
      asyncSearch
      fetchOnEmpty
      autoFocus={autoFocus}
      inputValue={search}
      onInputChange={(search, meta) => {
        if (meta.action !== 'input-change') return;
        setSearch(search || '');
      }}
      onFetch={(name, page) => {
        return getPriceItems({ page, name, categoryRevenueTypes }).then(
          ({ data, paging }) => {
            return {
              paging,
              data: data
                .filter((item) => !!item._id && !!item.name)
                .map((item) => ({
                  label: item.name,
                  value: item.name,
                  item,
                })),
            };
          },
        );
      }}
      cacheKey={[
        RevenuesCacheKeys.RevenuesPriceItemsDropdown,
        categoryRevenueTypes,
      ]}
      components={{ DropdownIndicator: null, Option }}
      isClearable={false}
      menuIsOpen
      wrapperClassName="flex-1 flex flex-col"
      themeOptions={{
        menuListClassName: 'min-h-full h-full !max-h-[0px]',
        containerClassName: 'flex-1 flex flex-col',
      }}
      classNames={{
        menu: () => 'mt-4 !static flex-1',
      }}
      placeholder={t('search')}
      onChange={(option: OptionType | null) => {
        if (!option) return;
        onItemSelect(option.item);
      }}
    />
  );
};
